import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../components/config/api';
import type { BasketItem } from '../types/order';
import type { TakeawayPaymentMethod } from '../utils/takeawayPayment';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TakeawayDraft {
  type: 'takeaway';
  basket: BasketItem[];
  customer: { name: string; notes?: string };
  // Αποθηκεύεται, αλλά η επαναφορά ΔΕΝ τον προεπιλέγει (βλ. TakeAway handleResumeDraft).
  paymentMethod: TakeawayPaymentMethod;
}

interface DeliveryDraft {
  type: 'delivery';
  phoneNumber: string;
  customer: Record<string, any> | null;
  selectedAddress: Record<string, any> | null;
}

export type OrderDraft = TakeawayDraft | DeliveryDraft;

export interface ParkedOrder {
  id: number;
  type: 'takeaway' | 'delivery';
  label: string;
  total: number;
  item_count: number;
  notes: string | null;
  created_at: string;
  parked_by_name?: string;
}

// Full draft including basket data — returned only when resuming
export interface ParkedOrderFull extends ParkedOrder {
  draft_data: OrderDraft;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOrderQueue(storeId: number | undefined) {
  const [queue, setQueue] = useState<ParkedOrder[]>([]);
  const [loading, setLoading] = useState(false);
  // Το τελευταίο token που είδαμε: όταν η σελίδα κλείνει ΜΕΤΑ την αποσύνδεση/αλλαγή χρήστη, το
  // καλάθι του Take Away στέλνεται στην αναμονή με τον χρήστη που το είχε — αλλιώς 401 και το καλάθι
  // χανόταν σιωπηλά (κατ. 25 του 144, 13 και 14/10/2026).
  const lastTokenRef = useRef(localStorage.getItem('xentrix_token') || '');
  const token = () => {
    const current = localStorage.getItem('xentrix_token') || '';
    if (current) lastTokenRef.current = current;
    return current || lastTokenRef.current;
  };

  const fetchQueue = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/order-drafts`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (res.ok) setQueue(await res.json());
    } catch { /* silent — queue not critical */ }
  }, [storeId]);

  // Load on mount
  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const park = useCallback(async (draft: OrderDraft, notes?: string): Promise<number | null> => {
    if (!storeId) return null;

    let label = 'Ανώνυμος';
    let total = 0;
    let item_count = 0;

    if (draft.type === 'takeaway') {
      label = draft.customer.name?.trim() || 'Ανώνυμος';
      total = draft.basket.reduce((s, i) => s + i.totalPrice, 0);
      item_count = draft.basket.reduce((s, i) => s + i.quantity, 0);
    } else {
      label = draft.customer?.name || draft.phoneNumber || 'Delivery';
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/order-drafts`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ type: draft.type, label, total, item_count, draft_data: draft, notes })
      });
      if (!res.ok) return null;
      const created = await res.json();
      setQueue(prev => [...prev, created]);
      return created.id;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const resume = useCallback(async (id: number): Promise<OrderDraft | null> => {
    if (!storeId) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/order-drafts/${id}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!res.ok) return null;
      const full: ParkedOrderFull = await res.json();
      // Remove from queue after loading
      await fetch(`${API_BASE_URL}/api/stores/${storeId}/order-drafts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      setQueue(prev => prev.filter(p => p.id !== id));
      return full.draft_data;
    } catch {
      return null;
    }
  }, [storeId]);

  const remove = useCallback(async (id: number) => {
    if (!storeId) return;
    try {
      await fetch(`${API_BASE_URL}/api/stores/${storeId}/order-drafts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      setQueue(prev => prev.filter(p => p.id !== id));
    } catch { /* silent */ }
  }, [storeId]);

  const updateNotes = useCallback(async (id: number, notes: string) => {
    if (!storeId) return;
    await fetch(`${API_BASE_URL}/api/stores/${storeId}/order-drafts/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ notes })
    });
    setQueue(prev => prev.map(p => p.id === id ? { ...p, notes } : p));
  }, [storeId]);

  return { queue, loading, park, resume, remove, updateNotes, refresh: fetchQueue };
}
